import { DSDialogModule } from '@aize/shuffle-ds';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http';
import { importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, TitleStrategy } from '@angular/router';
import { routes } from '@app/app.routes';
import { CustomDateAdapter } from '@app/core/shared/components/field-view/adapters/custom-date-adapter';
import { CUSTOM_DATE_FORMATS } from '@app/core/shared/helpers/date-time.util';
import { DateTimeInterceptor } from '@app/core/shared/interceptors/date-time.interceptor';
import { UserContextKind } from '@app/core/shared/models/context-kind';
import { FeatureFlagService } from '@app/core/shared/services/feature-flag/feature-flag.service';
import { provideTelemetry } from '@elementz/telemetry';
import { environment } from '@environment/environment';
import { ApplicationAuthService } from '@services/application-auth.service';
import { KeycloakAuthService } from '@services/keycloak-auth.service';
import { provideAuthentication } from '@shared/authentication';
import {
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  includeBearerTokenInterceptor,
  provideKeycloak
} from 'keycloak-angular';
import { BoostrapAuthConfigHelper } from '../../shared/BoostrapAuthConfigHelper';
import { AppTitleStrategy } from '@elementz/title-strategy';

export const SHARED_PROVIDERS = [
  provideRouter(routes),
  provideHttpClient(withInterceptorsFromDi(), withInterceptors([includeBearerTokenInterceptor])),
  provideAnimations(),
  provideAppInitializer(() => {
    const context: UserContextKind = {
      kind: 'user',
      key: 'anonymous',
      anonymous: true,
      tenantId: environment.auth0?.tenantId
    };
    return inject(FeatureFlagService).initialize(context);
  }),
  importProvidersFrom(DSDialogModule),
  provideKeycloak({
    config: {
      clientId: environment.keycloak?.clientId ?? 'ENV keycloak.clientId IS NOT SET',
      realm: environment.keycloak?.realm ?? 'ENV keycloak.realm IS NOT SET',
      url: environment.keycloak?.url ?? 'ENV keycloak.url IS NOT SET'
    }
  }),
  {
    provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
    useValue: environment.offline ? [{ urlPattern: new RegExp(`^${environment.api}`) }] : []
  },
  provideAuthentication(
    environment.offline
      ? BoostrapAuthConfigHelper.getOfflineAuthOptions()
      : BoostrapAuthConfigHelper.getTwinAuthOptions()
  ),
  {
    provide: ApplicationAuthService,
    useClass: environment.offline ? KeycloakAuthService : ApplicationAuthService
  },
  { provide: HTTP_INTERCEPTORS, useClass: DateTimeInterceptor, multi: true },
  BoostrapAuthConfigHelper.getKeycloakInitializerProvider(),
  { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', floatLabel: 'always' } },
  { provide: MAT_TABS_CONFIG, useValue: { animationDuration: '0ms' } },
  { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
  { provide: DateAdapter, useClass: CustomDateAdapter },
  { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
  {
    provide: MAT_DIALOG_DEFAULT_OPTIONS,
    useValue: { ...new MatDialogConfig(), disableClose: true, autoFocus: null }
  },
  BoostrapAuthConfigHelper.getApolloProvider(),
  provideTelemetry({
    connectionString: environment.appinsights?.connectionString,
    roleName: 'int-spa-elementz',
    roleInstance: 'int-spa-elementz-t419371935-elmz-local'
  }),
  {
    provide: TitleStrategy,
    useClass: AppTitleStrategy
  }
];
